/**
 * Quantity Limit Popup
 * Handles global quantity limit enforcement across all add-to-cart entry points
 */

class QuantityLimitPopup {
  constructor() {
    this.popup = document.getElementById('quantity-limit-popup');
    this.closeBtn = this.popup?.querySelector('.quantity-limit-popup__close');
    this.overlay = this.popup?.querySelector('.quantity-limit-popup__overlay');
    this.limit = window.NAWA_QUANTITY_LIMIT || 0;

    if (!this.popup || this.limit === 0) return;

    this.init();
  }

  init() {
    // Close button click
    this.closeBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.hide();
    });

    // Overlay click
    this.overlay?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.hide();
    });

    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.classList.contains('is-visible')) {
        e.stopPropagation();
        e.preventDefault();
        this.hide();
      }
    });

    // Intercept all add-to-cart attempts
    this.interceptAddToCart();
    this.interceptCartUpdates();
  }

  show() {
    this.popup.style.display = 'flex';
    // Trigger reflow to enable transition
    requestAnimationFrame(() => {
      this.popup.classList.add('is-visible');
    });
    document.body.classList.add('quantity-limit-popup-open');
  }

  hide() {
    this.popup.classList.remove('is-visible');
    document.body.classList.remove('quantity-limit-popup-open');
    setTimeout(() => {
      this.popup.style.display = 'none';
    }, 300);
  }

  async getCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart:', error);
      return { items: [] };
    }
  }

  async checkQuantityLimit(variantId, requestedQty = 1) {
    try {
      const cart = await this.getCart();
      const existingItem = cart.items.find(item => item.variant_id === parseInt(variantId));
      const currentQty = existingItem ? existingItem.quantity : 0;
      const totalQty = currentQty + requestedQty;

      return totalQty > this.limit;
    } catch (error) {
      console.error('Error checking quantity limit:', error);
      return false;
    }
  }

  interceptAddToCart() {
    // Store original fetch
    const originalFetch = window.fetch;
    const self = this;

    // Override fetch to intercept add-to-cart requests
    window.fetch = async function (...args) {
      const [url, options] = args;

      // Check if this is an add-to-cart request
      if (url && (url.includes('/cart/add') || url.includes('/cart/add.js'))) {
        try {
          let variantId = null;
          let quantity = 1;

          // Parse the request body
          if (options?.body) {
            if (typeof options.body === 'string') {
              try {
                // Try parsing as JSON
                const parsed = JSON.parse(options.body);
                variantId = parsed.id || parsed.items?.[0]?.id;
                quantity = parseInt(parsed.quantity || parsed.items?.[0]?.quantity || 1);
              } catch (e) {
                // Try parsing as FormData or URLSearchParams
                const params = new URLSearchParams(options.body);
                variantId = params.get('id');
                quantity = parseInt(params.get('quantity') || '1');
              }
            } else if (options.body instanceof FormData) {
              variantId = options.body.get('id');
              quantity = parseInt(options.body.get('quantity') || '1');
            }
          }

          // Check quantity limit
          if (variantId && self.limit > 0) {
            const limitExceeded = await self.checkQuantityLimit(variantId, quantity);

            if (limitExceeded) {
              self.show();
              // Return a rejected response that looks like a Shopify error
              const errorResponse = {
                status: 422,
                description: 'Quantity limit reached',
                message: 'You have reached the quantity limit for this item'
              };
              return Promise.resolve({
                ok: false,
                status: 422,
                json: () => Promise.resolve(errorResponse),
                text: () => Promise.resolve(JSON.stringify(errorResponse))
              });
            }
          }
        } catch (error) {
          console.error('Error intercepting add-to-cart:', error);
        }
      }

      // Continue with original fetch
      return originalFetch.apply(this, args);
    };

    // Intercept form submissions for traditional add-to-cart forms
    document.addEventListener('submit', async (e) => {
      const form = e.target;

      // Check if this is a product form
      if (form.matches('[action*="/cart/add"]') ||
        form.matches('product-form form') ||
        form.querySelector('[name="id"]')) {

        const variantId = form.querySelector('[name="id"]')?.value;
        const quantity = parseInt(form.querySelector('[name="quantity"]')?.value || '1');

        if (variantId && this.limit > 0) {
          const limitExceeded = await this.checkQuantityLimit(variantId, quantity);

          if (limitExceeded) {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.show();
            return false;
          }
        }
      }
    }, true); // Use capture phase

    // Listen for custom add-to-cart events (for theme compatibility)
    document.addEventListener('product:add-to-cart', async (e) => {
      const { variantId, quantity } = e.detail || {};

      if (variantId && this.limit > 0) {
        const limitExceeded = await this.checkQuantityLimit(variantId, quantity || 1);

        if (limitExceeded) {
          e.preventDefault();
          e.stopImmediatePropagation();
          this.show();
        }
      }
    }, true);
  }

  interceptCartUpdates() {
    // Monitor cart quantity input changes
    document.addEventListener('change', async (e) => {
      const input = e.target;

      // Check if this is a cart quantity input
      if (input.matches('[name="updates[]"]')) {
        const newQty = parseInt(input.value);

        if (this.limit > 0 && newQty > this.limit) {
          e.preventDefault();
          e.stopImmediatePropagation();

          // Reset to limit value
          input.value = this.limit;

          this.show();
          return false;
        }
      }
    }, true);

    // Monitor increment/decrement buttons
    document.addEventListener('click', async (e) => {
      const button = e.target.closest('button[name="plus"], button[name="increment"]');

      if (button) {
        const quantityInput = button.closest('quantity-input-dawn, .quantity')?.querySelector('input');

        if (quantityInput) {
          const currentValue = parseInt(quantityInput.value || 0);
          const step = parseInt(quantityInput.step || 1);
          const potentialValue = currentValue + step;

          if (this.limit > 0 && potentialValue > this.limit) {
            e.preventDefault();
            e.stopImmediatePropagation();
            this.show();
            return false;
          }
        }
      }
    }, true);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.nawaQuantityLimitPopup = new QuantityLimitPopup();
  });
} else {
  window.nawaQuantityLimitPopup = new QuantityLimitPopup();
}
